import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import api from '@/lib/api';
import { TrendingUp, TrendingDown, Target, Calendar } from 'lucide-react';
import logger from '@/lib/logger';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const formatBRL = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

function BreakEvenChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBreakeven = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/financial/breakeven');
      setData(res.data);
    } catch (e) {
      logger.error('Error fetching breakeven:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBreakeven(); }, [fetchBreakeven]);

  if (loading || !data) {
    return (
      <Card className="p-6 border border-gray-200">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-800"></div>
        </div>
      </Card>
    );
  }

  const labels = data.months.map(m => {
    const [y, mo] = m.month.split('-');
    const d = new Date(parseInt(y), parseInt(mo) - 1, 1);
    const txt = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    return txt.charAt(0).toUpperCase() + txt.slice(1).replace('.', '');
  });

  // Color each profit point: green if profitable, red if loss
  const profitPointColors = data.months.map(m =>
    m.profit > 0 ? 'rgb(22, 101, 52)' : m.profit < 0 ? 'rgb(220, 38, 38)' : 'rgb(107, 114, 128)'
  );

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Receitas',
        data: data.months.map(m => m.revenue),
        backgroundColor: 'rgba(22, 163, 74, 0.7)',
        borderRadius: 4,
        order: 2,
      },
      {
        type: 'bar',
        label: 'Despesas',
        data: data.months.map(m => m.expense),
        backgroundColor: 'rgba(220, 38, 38, 0.7)',
        borderRadius: 4,
        order: 2,
      },
      {
        type: 'line',
        label: 'Lucro / Prejuízo',
        data: data.months.map(m => m.profit),
        borderColor: 'rgb(30, 64, 175)',
        backgroundColor: 'rgba(30, 64, 175, 0.1)',
        borderWidth: 3,
        tension: 0.25,
        pointBackgroundColor: profitPointColors,
        pointBorderColor: profitPointColors,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
        order: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatBRL(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (v) => formatBRL(v).replace('R$\u00a0', 'R$ '),
        },
      },
    },
  };

  const { summary } = data;

  return (
    <div className="space-y-4" data-testid="breakeven-chart">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-700" />
            <div>
              <p className="text-xs text-emerald-700">Meses no lucro</p>
              <p className="text-xl font-bold text-emerald-800" data-testid="breakeven-profitable-months">
                {summary.profitable_months}<span className="text-xs ml-1">/12</span>
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-3 border border-red-200 bg-red-50">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-700" />
            <div>
              <p className="text-xs text-red-700">Meses no prejuízo</p>
              <p className="text-xl font-bold text-red-800" data-testid="breakeven-loss-months">
                {summary.loss_months}<span className="text-xs ml-1">/12</span>
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-3 border border-blue-200 bg-blue-50">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-700" />
            <div>
              <p className="text-xs text-blue-700">Ponto de equilíbrio mensal</p>
              <p className="text-lg font-bold text-blue-800" data-testid="breakeven-monthly">
                {formatBRL(summary.breakeven_monthly)}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-3 border border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-700" />
            <div>
              <p className="text-xs text-gray-600">Saldo acumulado (12m)</p>
              <p className={`text-lg font-bold ${summary.net_12m >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="breakeven-net-12m">
                {formatBRL(summary.net_12m)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Chart */}
      <Card className="p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-700" />
            Ponto de Equilíbrio Mensal
          </h3>
          <span className="text-xs text-gray-500">
            Barras = receitas/despesas reais · Linha = resultado (verde = lucro, vermelho = prejuízo)
          </span>
        </div>
        <div className="h-80">
          <Bar data={chartData} options={options} />
        </div>
      </Card>
    </div>
  );
}

export default BreakEvenChart;
